import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BillCard = ({ bill, onApprove, onPay, onEdit, onView, onDelete, onPause, onResume, type }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending_approval':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'approved':
        return 'bg-success/10 text-success border-success/20';
      case 'paid':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'partially_paid':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'overdue':
        return 'bg-error/10 text-error border-error/20';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilDue = getDaysUntilDue(bill?.due_date);
  const remainingBalance = bill?.balance_due || bill?.total_amount || 0;
  const isOverdue = daysUntilDue < 0;
  const isDueSoon = daysUntilDue >= 0 && daysUntilDue <= 7;

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-elevated transition-shadow duration-200">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-foreground">{bill?.bill_number}</h3>
          <p className="text-sm text-muted-foreground">{bill?.vendor?.vendor_name}</p>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(bill?.status)}`}>
          {bill?.status?.replace('_', ' ')}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-muted-foreground">Amount</p>
          <p className="font-semibold text-foreground">${(bill?.total_amount || 0)?.toLocaleString()}</p>
          {remainingBalance < bill?.total_amount && (
            <p className="text-xs text-primary">
              Balance: ${remainingBalance?.toLocaleString()}
            </p>
          )}
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Due Date</p>
          <p className="font-semibold text-foreground">
            {bill?.due_date ? new Date(bill.due_date)?.toLocaleDateString() : 'Not set'}
          </p>
          {isOverdue && (
            <p className="text-xs text-error">
              <Icon name="AlertCircle" size={12} className="inline mr-1" />
              {Math.abs(daysUntilDue)} days overdue
            </p>
          )}
          {isDueSoon && (
            <p className="text-xs text-warning">
              <Icon name="Clock" size={12} className="inline mr-1" />
              Due in {daysUntilDue} days
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="Tag" size={14} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {bill?.payment_terms?.replace('_', ' ') || 'Net 30'}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {/* Status-specific action buttons */}
          {bill?.status === 'draft' && (
            <Button variant="outline" size="sm" onClick={() => onEdit('edit', bill)}>
              <Icon name="Edit" size={14} className="mr-1" />
              Edit
            </Button>
          )}
          
          {bill?.status === 'pending_approval' && (
            <Button variant="success" size="sm" onClick={() => onApprove('approve', bill)}>
              <Icon name="Check" size={14} className="mr-1" />
              Approve
            </Button>
          )}
          
          {(bill?.status === 'approved' || (bill?.status === 'partially_paid' && remainingBalance > 0)) && (
            <Button variant="outline" size="sm" onClick={() => onPay('pay', bill)}>
              <Icon name="CreditCard" size={14} className="mr-1" />
              Pay
            </Button>
          )}
          
          {/* Recurring bill controls */}
          {type === 'recurring-bills' && bill?.status === 'approved' && (
            <Button variant="outline" size="sm" onClick={() => onPause('pause', bill)}>
              <Icon name="Pause" size={14} className="mr-1" />
              Pause
            </Button>
          )}
          
          {type === 'recurring-bills' && bill?.status === 'cancelled' && (
            <Button variant="outline" size="sm" onClick={() => onResume('resume', bill)}>
              <Icon name="Play" size={14} className="mr-1" />
              Resume
            </Button>
          )}

          {/* Universal actions */}
          <Button variant="ghost" size="sm" onClick={() => onView('view', bill)}>
            <Icon name="Eye" size={16} />
          </Button>
          
          {bill?.status !== 'paid' && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onDelete('delete', bill)}
              className="text-error hover:text-error hover:bg-error/10"
            >
              <Icon name="Trash" size={16} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillCard;